import React, { useState, useRef, useEffect } from "react";
import CommonTable from "components/ReactTable/Table";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Input,
  Row,
  Col,
  Container,
  Badge,
} from "reactstrap";
import Select, { components } from "react-select";
import {
  fixedWidthCustomStyle,
  MASTER_STATUS_OPTIONS,
  CREATED_BY,
  DEFAULT_RECORDS_PER_PAGE,
} from "components/Common/constants";
import NotificationAlert from "react-notification-alert";
import { dashboardOdooManufacturerNotSyncedList } from "actions/dashboard";
import { dashboardOdooQuotationsNotSyncedList } from "actions/dashboard";
import { useNavigate } from "react-router-dom";

const OdooManufacturer = () => {
  const navigate = useNavigate();
  const notificationAlertRef = useRef(null);

  const [pageNum, setPageNum] = useState(1);
  const [manufacturerListState, setManufacturerListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [filterState, setFilterState] = useState({
    searchName: "",
    searchStatus: null,
    createdBy: null,
    filterParams: [],
  });

  // ✅ Store the Odoo API response in local state
  const [odooResponse, setOdooResponse] = useState(null);

  // --- Build filter params from UI state ---
  const buildFilterParams = () => {
    const filterParams = [];

    if (filterState.searchName) {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filterState.searchName],
      });
    }

    if (filterState.createdBy?.value) {
      filterParams.push({
        field: "CreatedBy",
        condition: "eq",
        filterValues: [filterState.createdBy.value],
      });
    }

    if (filterState.searchStatus) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.searchStatus.value],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : undefined;
  };

  // --- Fetch Odoo not-synced manufacturers list ---
  useEffect(() => {
    const fetchData = async () => {
      setManufacturerListState((prev) => ({ ...prev, isLoading: true }));

      const filters = filterState.filterParams?.filters;
      const searchString = filterState.filterParams?.searchString || "";

      try {
        const res = await dashboardOdooManufacturerNotSyncedList({
          pageNum,
          searchString,
          filters:
            filters && filters !== "[]" && filters.length > 2
              ? filters
              : undefined,
        });
        setOdooResponse(res?.data || res);
      } catch (err) {
        setOdooResponse({
          code: 500,
          message: "Failed to load the manufacturers list! Please try again.",
          data: null,
        });
      }
    };

    fetchData();
  }, [pageNum, filterState.filterParams]);

  // --- Sync loader / error state from the response ---
  useEffect(() => {
    if (!odooResponse) return;

    if (odooResponse.code === 200) {
      setManufacturerListState({
        isLoading: false,
        error: "",
        noRecords: (odooResponse?.data?.manufacturer?.length ?? 0) === 0,
        showLoaderOnClick: false,
      });
    } else if (odooResponse.code || odooResponse.message) {
      setManufacturerListState({
        isLoading: false,
        error:
          odooResponse.message ||
          "Failed to load the manufacturers list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [odooResponse]);

  const formattedManufacturerList =
    odooResponse?.data?.manufacturer?.map((item, index) => ({
      ...item,
      srNo:
        (pageNum - 1) *
          (odooResponse?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    })) ?? [];

  const totalRecords = odooResponse?.data?.totalRecords || 0;
  const totalFilteredRecords = odooResponse?.data?.totalFilteredRecords || 0;
  const noOfRecordsPerPage =
    odooResponse?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE;

  const handleSearch = () => {
    const searchString = filterState.searchName.trim();

    if (
      !searchString &&
      !filterState.searchStatus &&
      !filterState.createdBy
    ) {
      return;
    }

    const newfilterParams = {
      filters: buildFilterParams(),
      searchString,
    };

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newfilterParams)
    ) {
      setManufacturerListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilterState((prev) => ({
        ...prev,
        filterParams: newfilterParams,
      }));
      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleClear = () => {
    setManufacturerListState((prev) => ({ ...prev, isLoading: true }));
    setFilterState({
      searchName: "",
      searchStatus: null,
      createdBy: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  // --- Table columns ---
  const columns = [
    { Header: "Sr. no.", accessor: "srNo", className: "col-srNo" },
    {
      Header: "Manufacturer Name",
      accessor: "name",
      className: "col-mfr-name",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Created By",
      accessor: "createdBy",
      className: "col-mfr-createdBy",
      Cell: ({ value }) =>
        CREATED_BY.find((item) => item.value === value)?.label || "-",
    },
    {
      Header: "Status",
      accessor: "status",
      className: "col-mfr-status",
      Cell: ({ value }) => (
        <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
          {value || "-"}
        </Badge>
      ),
    },
  ];

  // Custom renderers for the Status dropdown
  const Option = (props) => (
    <components.Option {...props}>
      <Badge color={props.data.value === "ACTIVE" ? "success" : "warning"} pill>
        {props.data.label}
      </Badge>
    </components.Option>
  );

  const SingleValue = (props) => (
    <components.SingleValue {...props}>
      <Badge color={props.data.value === "ACTIVE" ? "success" : "warning"} pill>
        {props.data.label}
      </Badge>
    </components.SingleValue>
  );

  return (
    <>
      <Container fluid className="content mt-4">
        <div className="rna-container">
          <NotificationAlert ref={notificationAlertRef} />
        </div>

        <Row>
          <Col md="12">
            <Card className="card-user">
              <CardHeader className="mb-2">
                <div className="d-flex flex-column align-items-start">
                <CardTitle tag="h3" className="font-weight-normal">
                  Odoo Manufacturers — Not Synced
                </CardTitle>
                </div>
              </CardHeader>

              <CardBody>
                {/* Filters */}
                <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by manufacturer"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={CREATED_BY}
                      placeholder="Created by"
                      value={filterState.createdBy}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          createdBy: selected,
                        }))
                      }
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={MASTER_STATUS_OPTIONS}
                      placeholder="Status"
                      value={filterState.searchStatus}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchStatus: selected,
                        }))
                      }
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
                      onClick={handleSearch}
                    >
                      {manufacturerListState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin search-loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {filterState.filterParams?.filters?.length > 0 && (
                      <Button
                        color="default"
                        className="d-flex align-items-center mb-3 filter-btn btn-simple mt-2"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Records count */}
                <div className="mt-2 mb-3 mx-1">
                  {totalRecords > 0 &&
                    (filterState.filterParams?.filters?.length > 0 ||
                    filterState.filterParams?.searchString?.trim() ? (
                      <span className="font-weight-bold">
                        Showing {totalFilteredRecords} of {totalRecords}{" "}
                        Manufacturer
                      </span>
                    ) : (
                      <span className="font-weight-bold">
                        Total Manufacturer : {totalRecords}
                      </span>
                    ))}
                </div>

                {/* Table */}
                {manufacturerListState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : manufacturerListState.error ? (
                  <div className="loaderContainer">
                    {manufacturerListState.error}
                  </div>
                ) : manufacturerListState.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No manufacturers found.
                  </div>
                ) : (
                  <div className="table-responsive manufacturer-table">
                    <CommonTable
                      showPaginationBottom={true}
                      defaultPageSize={noOfRecordsPerPage}
                      activePage={pageNum}
                      data={formattedManufacturerList}
                      resizable={false}
                      columns={columns}
                      className="-striped -highlight table-hover"
                      totalPages={Math.ceil(
                        totalFilteredRecords / noOfRecordsPerPage
                      )}
                      setPageNum={setPageNum}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default OdooManufacturer;